import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ForgotPasswordBuilder } from './forgot-password.builder';
import {
  filter,
  flatMap,
  map,
  shareReplay,
  take,
  takeUntil,
} from 'rxjs/operators';
import { AccountDispatcher } from '../../store/dispatchers';
import { Observable, Subject, combineLatest } from 'rxjs';
import { LocalizationStore } from '../../../localization/store/stores';
import { AccountStore } from '../../store/stores';
import { IAccountResponseError } from '../../account.data';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';
import { AlignmentEnum, IconSizesEnum, IIcon, IRouter } from 'i2g-ng-ui';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [ForgotPasswordBuilder],
})
export class ForgotPasswordComponent
  implements OnInit, OnDestroy, AfterViewInit {
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  error$: Observable<string | Error | IAccountResponseError>;
  processing$: Observable<boolean>;
  setPasswordMessage$: Observable<string>;
  registerRoute: IRouter;
  loginRoute: IRouter;
  backIcon: IIcon;
  errorIcon: IIcon;
  trackingScreenName: TrackingScreenNamesEnum;
  trackingObject$: Observable<ITrackingObject>;
  forgotPasswordTrackingAction: ITrackingAction;
  loginTrackingAction: ITrackingAction;
  alignmentEnum: typeof AlignmentEnum;
  formBannerExist$: Observable<boolean>;

  constructor(
    private _forgotPasswordBuilder: ForgotPasswordBuilder,
    private _accountStore: AccountStore,
    private _accountDispatchers: AccountDispatcher,
    private _localizationStore: LocalizationStore,
    private _trackingService: I2gNgTrackingService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnInit() {
    this.trackingScreenName = TrackingScreenNamesEnum.forgot_password;
    this.trackingObject$ = this._accountStore
      .getTrackingObject()
      .pipe(shareReplay(1));
    this.forgotPasswordTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'forgot_password_submit',
      this.trackingScreenName
    );
    this.loginTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'back_to_sign_in',
      this.trackingScreenName
    );

    this.backIcon = {
      name: 'chevron-left',
      size: IconSizesEnum.x_small,
    };
    this.errorIcon = {
      name: 'error',
      size: IconSizesEnum.small,
    };
    this.alignmentEnum = AlignmentEnum;

    this.registerRoute = this._forgotPasswordBuilder.buildRegisterRoute();

    this.processing$ = this._accountStore
      .isSetPasswordProcessing()
      .pipe(shareReplay(1));

    this.error$ = this._accountStore.getSetPasswordError().pipe(
      map((error: Error | IAccountResponseError) => {
        if (error) {
          return error instanceof Error ? error.message : error.description;
        }
        return error;
      }),
      shareReplay(1)
    );

    this.setPasswordMessage$ = this._accountStore
      .getSetPasswordMessage()
      .pipe(shareReplay(1));

    this.formBannerExist$ = combineLatest(
      this.error$,
      this.setPasswordMessage$
    ).pipe(
      map(([error, setPasswordMessage]) => !!(error || setPasswordMessage))
    );
  }

  ngAfterViewInit() {
    this._trackingService.trackPage(this.trackingScreenName);
  }

  sendSetPasswordEmail(email: string) {
    this.processing$
      .pipe(
        takeUntil(this._destroyed),
        filter((processing) => !processing),
        flatMap(() => this._localizationStore.getCurrentLocale()),
        take(1)
      )
      .subscribe((locale: string) =>
        this._accountDispatchers.setPasswordByEmail(email, locale)
      );
  }

  goToLogin() {
    this._accountDispatchers.goToLogin();
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
